import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Bell, Calendar, Clock, Mail, X, Loader2 } from "lucide-react";
import { API_URL } from "../config/api";


export function ReminderButton({ 
  referenceType, 
  referenceNumber, 
  getAuthHeaders, 
  buttonVariant = "outline",
  buttonSize = "sm"
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("09:00");
  const [note, setNote] = useState("");
  const [additionalEmails, setAdditionalEmails] = useState("");

  const handleCreateReminder = async () => {
    if (!reminderDate) {
      toast.error("Please select a date");
      return;
    }

    setLoading(true);
    try {
      const reminderDatetime = `${reminderDate}T${reminderTime}:00`;
      const emails = additionalEmails.split(/[,;\s]+/).filter(e => e.trim());

      await axios.post(
        `${API_URL}/reminders`,
        {
          reference_type: referenceType,
          reference_number: referenceNumber,
          reminder_datetime: reminderDatetime,
          note: note || null,
          notification_emails: emails
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Reminder set for ${new Date(reminderDatetime).toLocaleString()}`);
      setOpen(false);
      setReminderDate("");
      setReminderTime("09:00");
      setNote("");
      setAdditionalEmails("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create reminder");
    } finally {
      setLoading(false);
    }
  };

  // Calculate min date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={buttonVariant} 
          size={buttonSize}
          title="Set reminder"
          data-testid={`reminder-btn-${referenceNumber}`}
        >
          <Bell className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Set Reminder
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Reference Info */}
          <div className="bg-secondary/30 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">
              {referenceType === "container" ? "Container" : referenceType === "hbl" ? "HBL" : "Tracking"}
            </p>
            <p className="font-mono font-bold text-foreground">{referenceNumber}</p>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Date
              </Label>
              <Input
                type="date"
                min={today}
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="reminder-date-input"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Time
              </Label>
              <Input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                data-testid="reminder-time-input"
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">
              Note (optional)
            </Label>
            <Input
              type="text"
              placeholder="e.g., Check customs clearance status"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="reminder-note-input"
            />
          </div>

          {/* Additional Emails */}
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Additional notification emails (optional)
            </Label>
            <Input
              type="text"
              placeholder="colleague@example.com"
              value={additionalEmails}
              onChange={(e) => setAdditionalEmails(e.target.value)}
              data-testid="reminder-emails-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReminder}
              disabled={loading || !reminderDate}
              data-testid="create-reminder-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Set Reminder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RemindersList({ reminders, getAuthHeaders, onUpdate }) {
  const [loading, setLoading] = useState({});

  const handleDelete = async (reminderId) => {
    if (!window.confirm("Delete this reminder?")) return;

    setLoading(prev => ({ ...prev, [reminderId]: true }));
    try {
      await axios.delete(`${API_URL}/reminders/${reminderId}`, { headers: getAuthHeaders() });
      toast.success("Reminder deleted");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to delete reminder");
    } finally {
      setLoading(prev => ({ ...prev, [reminderId]: false }));
    }
  };

  if (!reminders || reminders.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No reminders set</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reminders.map((reminder) => (
        <div
          key={reminder.id}
          className={`flex items-center justify-between p-3 rounded-lg border ${
            reminder.status === "sent" 
              ? "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800" 
              : "bg-secondary/30 border-border"
          }`}
        >
          <div className="flex items-center gap-3">
            <Bell className={`h-4 w-4 ${reminder.status === "sent" ? "text-green-500" : "text-primary"}`} />
            <div>
              <p className="font-mono text-sm font-medium text-foreground">
                {reminder.reference_number}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(reminder.reminder_datetime).toLocaleString()}
                {reminder.note && ` • ${reminder.note}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded ${
              reminder.status === "sent" 
                ? "bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200" 
                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200"
            }`}>
              {reminder.status}
            </span>
            {reminder.status === "pending" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(reminder.id)}
                disabled={loading[reminder.id]}
                className="text-red-500 hover:text-red-600"
              >
                {loading[reminder.id] ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
